import { useState } from 'react';
import { useKmagic } from './useKmagic';

/**
 * Mobile kMagic - a chat-style layout: the "conversation" (intro → progress →
 * result) scrolls above, and a fixed composer at the bottom holds the custom
 * instruction box, the primary action, and a ⋮ menu (Reset / Roll back / Run
 * again). Shares all run logic with the desktop panel via useKmagic().
 */
export function StudioMobileKmagic() {
  const {
    projectId, instruction, setInstruction, phase, inPreview, pct, quotaError, runningMessage,
    result: r, start, showResult, applyResult, rollback, reset, runAgain,
  } = useKmagic();
  const [menu, setMenu] = useState(false);

  // Primary composer action adapts to the run phase.
  let primary: { label: string; onClick: () => void; disabled?: boolean };
  if (phase === 'running') primary = { label: `${pct}%`, onClick: () => {}, disabled: true };
  else if (phase === 'ready' && !inPreview) primary = { label: 'Show', onClick: showResult };
  else if (phase === 'previewing') primary = { label: 'Apply', onClick: applyResult };
  else primary = { label: '✦ Run', onClick: start, disabled: !projectId };

  return (
    <div className="mkm">
      {/* Conversation / status */}
      <div className="mkm-feed">
        <div className="mkm-msg">
          <span className="mkm-k">kMagic</span>
          <p>AI art-directs your whole edit.</p>
        </div>

        {phase === 'running' && (
          <div className="mkm-msg mkm-running">
            <div className="kmg-progress"><div className="kmg-progress-fill" style={{ width: `${Math.max(6, pct)}%` }} /></div>
            <p className="mkm-dim mkm-status" key={runningMessage}>{runningMessage} · {pct}%</p>
          </div>
        )}

        {(phase === 'ready' || phase === 'previewing') && r && (
          <div className="mkm-msg mkm-result">
            <span className="kmg-badge">✦ Result ready</span>
            {r.rationale && <p className="kmg-rationale">{r.rationale}</p>}
            <ul className="kmg-summary">
              {r.templateId && <li><b>Style</b><span>{r.templateId}</span></li>}
              <li><b>Emphasis</b><span>{r.emphasisWordIds?.length ?? 0} words</span></li>
              <li><b>Hooks</b><span>{r.hooks?.length ? `${r.hooks.length} · ${r.hooks.map((h) => h.text).join(', ')}` : '-'}</span></li>
              <li><b>Transitions</b><span>{r.transitions?.length ?? 0}</span></li>
              <li><b>B‑roll</b><span>{r.brolls?.length ? `${r.brolls.length} clip${r.brolls.length > 1 ? 's' : ''}` : (r.brollKeywords?.length ? 'none found' : '-')}</span></li>
              <li><b>Music</b><span>{r.music?.name ?? (r.musicKeywords?.length ? 'none found' : '-')}</span></li>
            </ul>
            {phase === 'previewing' && <p className="mkm-dim">Previewing on your video — Apply to keep it.</p>}
          </div>
        )}

        {quotaError && (
          <div className="mkm-msg">
            <p className="mkm-dim" style={{ color: '#c2503f' }}>
              {quotaError} <a href="/billing" style={{ color: '#c25e83', fontWeight: 600 }}>Upgrade →</a>
            </p>
          </div>
        )}
      </div>

      {/* Composer: instruction + primary action + ⋮ menu */}
      <div className="mkm-composer">
        <textarea
          className="mkm-input"
          placeholder="Tell kMagic what to do (optional)…"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          disabled={phase === 'running'}
          rows={1}
        />
        <button className="mkm-send" onClick={primary.onClick} disabled={primary.disabled}>{primary.label}</button>
        <div className="mkm-more-wrap">
          <button className="mkm-more" onClick={() => setMenu((v) => !v)} aria-label="More" aria-expanded={menu}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
          </button>
          {menu && (
            <>
              <div className="mkm-menu-backdrop" onClick={() => setMenu(false)} />
              <div className="mkm-menu" role="menu">
                {phase === 'previewing' && (
                  <button role="menuitem" onClick={() => { setMenu(false); rollback(); }}>↩ Roll back</button>
                )}
                {(phase === 'ready' || phase === 'previewing') && (
                  <button role="menuitem" onClick={() => { setMenu(false); runAgain(); }}>↻ Run again</button>
                )}
                <button role="menuitem" onClick={() => { setMenu(false); reset(); }} disabled={!projectId}>↺ Reset to original</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
